import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  createEquipmentFromTemplate,
} from "../redux/slices/equipmentTemplatesSlice";
import { fetchParts } from "../redux/slices/partsSlice";
import { fetchBrands, fetchCategories } from "../redux/slices/equipmentSlice";
import { equipmentTemplatesAPI } from "../api/api";
import { erpFailure } from "../utils/erpBooking";
import {
  Modal,
  ConfirmDialog,
  LoadingSpinner,
  EmptyState,
  DatalistInput,
  PartsListTable,
} from "../components/Common";
import { useSearch } from "../context/SearchContext";
import { useLanguage } from "../context/LanguageContext";
import Select from "react-select";
import { printTable } from "../utils/printTable";

// Mirrors PartsListTable but for required component templates (sub-assemblies)
const ComponentsListTable = ({ components, onRemove, onUpdate, showActions = true }) => {
  const { t } = useLanguage();

  if (!components || components.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <p className="text-gray-500">{t("equipmentTemplates.noComponentsAdded")}</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
              {t("equipmentTemplates.component")}
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
              {t("equipmentTemplates.qty")}
            </th>
            {showActions && (
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
                {t("common.actions")}
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {components.map((c, index) => (
            <tr key={c.component_template_id ?? index}>
              <td className="px-4 py-2 text-sm font-medium">
                {c.template_name || `#${c.component_template_id}`}
              </td>
              <td className="px-4 py-2 text-sm">
                {showActions ? (
                  <input
                    type="number"
                    min="1"
                    value={c.quantity ?? 1}
                    onChange={(e) => onUpdate(index, e.target.value)}
                    className="input-field w-20 text-center"
                  />
                ) : (
                  c.quantity
                )}
              </td>
              {showActions && (
                <td className="px-4 py-2 text-sm text-right">
                  <button
                    type="button"
                    onClick={() => onRemove(index)}
                    className="text-red-600 hover:text-red-900"
                  >
                    {t("common.remove")}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EquipmentTemplates = () => {
  const dispatch = useDispatch();
  const { searchTerm } = useSearch();
  const { t } = useLanguage();

  // Redux state
  const { items: templates, loading } = useSelector(
    (state) => state.equipmentTemplates,
  );
  const { items: parts } = useSelector((state) => state.parts);
  const { brands, categories } = useSelector((state) => state.equipment);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateEquipmentModal, setShowCreateEquipmentModal] =
    useState(false);
  // Create modal reused for "Duplicate" -- only changes its title
  const [isDuplicating, setIsDuplicating] = useState(false);

  // State
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [equipmentQuantity, setEquipmentQuantity] = useState(1);
  const [equipmentSerialNumbers, setEquipmentSerialNumbers] = useState([""]);
  const [creatingEquipment, setCreatingEquipment] = useState(false);
  const creatingEquipmentRef = useRef(false);
  const [isFetching, setIsFetching] = useState(false);

  // Part selector state
  const [selectedPart, setSelectedPart] = useState(null);
  const [partQuantity, setPartQuantity] = useState(1);

  // Component selector state (templates flagged is_component required by this template)
  const [selectedComponentTemplate, setSelectedComponentTemplate] = useState(null);
  const [componentQuantity, setComponentQuantity] = useState(1);

  // Form
  const initialFormData = {
    name: "",
    description: "",
    brand: "",
    category: "",
    article_id: "",
    parts_data: [],
    is_component: false,
    components_data: [],
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    dispatch(fetchTemplates());
    dispatch(fetchParts());
    dispatch(fetchBrands());
    dispatch(fetchCategories());
  }, [dispatch]);

  const filteredTemplates = templates.filter((template) => {
    return (
      template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handlePrintList = () => {
    printTable({
      title: t("equipmentTemplates.title"),
      columns: [
        { header: "#", align: "right" },
        { header: t("common.name") },
        { header: t("equipmentTemplates.brand") },
        { header: t("common.category") },
        { header: t("equipmentTemplates.articleId") },
        { header: t("equipmentTemplates.parts"), align: "right" },
      ],
      rows: filteredTemplates.map((tpl, i) => [
        i + 1,
        tpl.name,
        tpl.brand,
        tpl.category,
        tpl.article_id,
        tpl.parts_data?.length || 0,
      ]),
    });
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedPart(null);
    setPartQuantity(1);
    setSelectedComponentTemplate(null);
    setComponentQuantity(1);
    setIsDuplicating(false);
  };

  const openEditModal = async (template) => {
    setIsFetching(true);
    try {
      const response = await equipmentTemplatesAPI.getById(template.id);
      const fullData = response.data.data;

      const parsedParts =
        typeof fullData.parts_data === "string"
          ? JSON.parse(fullData.parts_data)
          : fullData.parts_data || [];

      setSelectedTemplate(fullData);
      setFormData({
        name: fullData.name || "",
        description: fullData.description || "",
        brand: fullData.brand || "",
        category: fullData.category || "",
        article_id: fullData.article_id || "",
        parts_data: parsedParts,
        is_component: !!fullData.is_component,
        components_data: fullData.components_data || [],
      });
      setSelectedPart(null);
      setPartQuantity(1);
      setSelectedComponentTemplate(null);
      setComponentQuantity(1);
      setShowEditModal(true);
    } catch (err) {
      alert(t("equipmentTemplates.errorFetchingDetails", { message: err.message }));
    } finally {
      setIsFetching(false);
    }
  };

  // Opens the Create modal prefilled with a copy of the template (name,
  // parts, components...) so it can be tweaked before saving as a new one.
  const openDuplicateModal = async (template) => {
    setIsFetching(true);
    try {
      const response = await equipmentTemplatesAPI.getById(template.id);
      const fullData = response.data.data;

      const parsedParts =
        typeof fullData.parts_data === "string"
          ? JSON.parse(fullData.parts_data)
          : fullData.parts_data || [];

      setSelectedTemplate(null);
      setFormData({
        name: `${fullData.name || ""} ${t("equipmentTemplates.copySuffix")}`.trim(),
        description: fullData.description || "",
        brand: fullData.brand || "",
        category: fullData.category || "",
        article_id: fullData.article_id || "",
        parts_data: parsedParts.map((p) => ({ ...p })),
        is_component: !!fullData.is_component,
        components_data: (fullData.components_data || []).map((c) => ({ ...c })),
      });
      setSelectedPart(null);
      setPartQuantity(1);
      setSelectedComponentTemplate(null);
      setComponentQuantity(1);
      setIsDuplicating(true);
      setShowCreateModal(true);
    } catch (err) {
      alert(t("equipmentTemplates.errorFetchingDetails", { message: err.message }));
    } finally {
      setIsFetching(false);
    }
  };

  const openDetailsModal = async (template) => {
    setIsFetching(true);
    try {
      const response = await equipmentTemplatesAPI.getById(template.id);
      const fullData = response.data.data;

      if (typeof fullData.parts_data === "string") {
        fullData.parts_data = JSON.parse(fullData.parts_data);
      }
      setSelectedTemplate(fullData);
      setShowDetailsModal(true);
    } catch (err) {
      alert(t("equipmentTemplates.errorLoadingDetails", { message: err.message }));
    } finally {
      setIsFetching(false);
    }
  };

  // ── Fixed: fetch full template data so parts_data with quantities is available ──
  const openCreateEquipmentModal = async (template) => {
    setIsFetching(true);
    try {
      const response = await equipmentTemplatesAPI.getById(template.id);
      const fullData = response.data.data;

      if (typeof fullData.parts_data === "string") {
        fullData.parts_data = JSON.parse(fullData.parts_data);
      }

      setSelectedTemplate(fullData);
      setEquipmentQuantity(1);
      setEquipmentSerialNumbers([""]);
      setShowCreateEquipmentModal(true);
    } catch (err) {
      alert(t("equipmentTemplates.errorLoadingTemplate", { message: err.message }));
    } finally {
      setIsFetching(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (formData.parts_data.length === 0) {
      alert(t("equipmentTemplates.pleaseAddPart"));
      return;
    }
    try {
      await dispatch(createTemplate(formData)).unwrap();
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      alert(t("equipmentTemplates.failedToCreate", { error: err }));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(
        updateTemplate({ id: selectedTemplate.id, data: formData }),
      ).unwrap();
      setShowEditModal(false);
      setSelectedTemplate(null);
      resetForm();
    } catch (err) {
      alert(t("equipmentTemplates.failedToUpdate", { error: err }));
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteTemplate(selectedTemplate.id)).unwrap();
      setShowDeleteDialog(false);
      setSelectedTemplate(null);
    } catch (err) {
      alert(t("equipmentTemplates.failedToDelete", { error: err }));
    }
  };

  // ── Fixed: pass parts_data (with quantities) to the backend ──
  const handleCreateEquipment = async () => {
    // Ref, not state: a fast double-click fires twice before a state update
    // would re-render the button as disabled.
    if (creatingEquipmentRef.current) return;

    const serials = equipmentSerialNumbers.map((s) => s.trim() || null);
    const missing = serials.filter((s) => !s).length;
    // Several rows with some left blank is usually a mistake (e.g. "+ add"
    // clicked and the serial typed into the new row) -- confirm first.
    if (
      !selectedTemplate.is_component &&
      serials.length > 1 &&
      missing > 0 &&
      !window.confirm(
        t("equipment.confirmMissingSerials", { count: serials.length, missing }),
      )
    ) {
      return;
    }

    creatingEquipmentRef.current = true;
    setCreatingEquipment(true);
    try {
      const action = await dispatch(
        createEquipmentFromTemplate({
          templateId: selectedTemplate.id,
          data: {
            serial_numbers: serials,
            parts_data: selectedTemplate.parts_data,
          },
        }),
      );
      // Not .unwrap(): that would drop action.meta, which carries the ERP outcome.
      if (createEquipmentFromTemplate.rejected.match(action)) {
        throw action.payload ?? action.error?.message;
      }
      const erpProblem = erpFailure(action.meta?.erp);
      alert(
        selectedTemplate.is_component
          ? t("equipmentTemplates.componentStockAddedSuccess")
          : t("equipmentTemplates.equipmentCreatedSuccess"),
      );
      if (erpProblem) {
        alert(t("equipmentTemplates.erpBookingFailed", erpProblem));
      }
      setShowCreateEquipmentModal(false);
      setEquipmentQuantity(1);
      setEquipmentSerialNumbers([""]);
      setSelectedTemplate(null);
    } catch (err) {
      alert(t("equipmentTemplates.failedToCreateEquipment", { error: err }));
    } finally {
      creatingEquipmentRef.current = false;
      setCreatingEquipment(false);
    }
  };

  const handleEquipmentQuantityChange = (rawValue) => {
    const quantity = Math.max(1, parseInt(rawValue, 10) || 1);
    setEquipmentQuantity(quantity);
    setEquipmentSerialNumbers((prev) => {
      const serials = prev.slice(0, quantity);
      while (serials.length < quantity) serials.push("");
      return serials;
    });
  };

  const addEquipmentSerialField = () => {
    setEquipmentQuantity((prev) => prev + 1);
    setEquipmentSerialNumbers((prev) => [...prev, ""]);
  };

  const removeEquipmentSerialField = (index) => {
    setEquipmentSerialNumbers((prev) => {
      if (prev.length <= 1) return prev;
      const serials = prev.filter((_, i) => i !== index);
      setEquipmentQuantity(serials.length);
      return serials;
    });
  };

  const updateEquipmentSerialNumber = (index, value) => {
    setEquipmentSerialNumbers((prev) => {
      const serials = [...prev];
      serials[index] = value;
      return serials;
    });
  };

  // ── Parts helpers ──────────────────────────────────────────
  const handleAddPart = () => {
    if (!selectedPart) return;
    const qty = Math.max(1, Number(partQuantity) || 1);

    setFormData((prev) => {
      const exists = prev.parts_data.findIndex(
        (p) => (p.part_id || p.id) === selectedPart.id,
      );

      if (exists !== -1) {
        const updated = [...prev.parts_data];
        updated[exists] = {
          ...updated[exists],
          quantity: (updated[exists].quantity || 1) + qty,
        };
        return { ...prev, parts_data: updated };
      }

      return {
        ...prev,
        parts_data: [
          ...prev.parts_data,
          {
            part_id: selectedPart.id,
            id: selectedPart.id,
            name: selectedPart.name,
            sku: selectedPart.sku || "",
            color: selectedPart.color || "",
            purchase_price: selectedPart.purchase_price || 0,
            quantity: qty,
          },
        ],
      };
    });

    setSelectedPart(null);
    setPartQuantity(1);
  };

  const removePart = (index) => {
    setFormData((prev) => ({
      ...prev,
      parts_data: prev.parts_data.filter((_, i) => i !== index),
    }));
  };

  const updatePartQuantity = (index, quantity) => {
    setFormData((prev) => {
      const updated = [...prev.parts_data];
      updated[index] = {
        ...updated[index],
        quantity: Math.max(1, Number(quantity)),
      };
      return { ...prev, parts_data: updated };
    });
  };
  // ──────────────────────────────────────────────────────────

  // ── Component helpers (required sub-assembly templates) ────
  const handleAddComponent = () => {
    if (!selectedComponentTemplate) return;
    const qty = Math.max(1, Number(componentQuantity) || 1);

    setFormData((prev) => {
      const exists = prev.components_data.findIndex(
        (c) => c.component_template_id === selectedComponentTemplate.id,
      );

      if (exists !== -1) {
        const updated = [...prev.components_data];
        updated[exists] = {
          ...updated[exists],
          quantity: (updated[exists].quantity || 1) + qty,
        };
        return { ...prev, components_data: updated };
      }

      return {
        ...prev,
        components_data: [
          ...prev.components_data,
          {
            component_template_id: selectedComponentTemplate.id,
            template_name: selectedComponentTemplate.name,
            quantity: qty,
          },
        ],
      };
    });

    setSelectedComponentTemplate(null);
    setComponentQuantity(1);
  };

  const removeComponent = (index) => {
    setFormData((prev) => ({
      ...prev,
      components_data: prev.components_data.filter((_, i) => i !== index),
    }));
  };

  const updateComponentQuantity = (index, quantity) => {
    setFormData((prev) => {
      const updated = [...prev.components_data];
      updated[index] = {
        ...updated[index],
        quantity: Math.max(1, Number(quantity)),
      };
      return { ...prev, components_data: updated };
    });
  };
  // ──────────────────────────────────────────────────────────

  // ── Shared Select styles ───────────────────────────────────
  const partSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "0.375rem",
      borderColor: state.isFocused ? "#3B82F6" : "#D1D5DB",
      boxShadow: state.isFocused ? "0 0 0 1px #3B82F6" : "none",
      minHeight: "42px",
      "&:hover": { borderColor: "#3B82F6" },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3B82F6"
        : state.isFocused
          ? "#EFF6FF"
          : "white",
      color: state.isSelected ? "white" : "#1F2937",
      cursor: "pointer",
      padding: "8px 12px",
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    placeholder: (base) => ({ ...base, color: "#9CA3AF" }),
  };

  const partSelectOptions = parts.map((p) => ({
    value: p.id,
    label: `${p.name}${p.color ? ` (${p.color})` : ""} - ${p.sku || "No SKU"}`,
    originalPart: p,
  }));

  const formatPartOptionLabel = (option) => {
    const p = option.originalPart;
    if (!p) return option.label;
    return (
      <div className="flex justify-between items-center">
        <div>
          <span className="font-medium">{p.name}</span>
          {p.color && <span className="text-gray-500 ml-1">({p.color})</span>}
          {p.sku && <span className="text-gray-400 text-xs ml-2">{p.sku}</span>}
        </div>
        <div className="text-right text-sm">
          <span className="text-green-600 font-medium">
            CHF {p.purchase_price || 0}
          </span>
          <span className="text-gray-400 ml-2">Stock: {p.quantity || 0}</span>
        </div>
      </div>
    );
  };

  // ── Part selector JSX (inline, NOT a nested component) ────
  const partSelectorJSX = (
    <div className="space-y-2">
      <div className="flex gap-2 items-center">
        <div className="flex-1 min-w-0">
          <Select
            classNamePrefix="react-select"
            options={partSelectOptions}
            value={
              selectedPart
                ? {
                    value: selectedPart.id,
                    label: `${selectedPart.name}${selectedPart.color ? ` (${selectedPart.color})` : ""} - ${selectedPart.sku || "No SKU"}`,
                    originalPart: selectedPart,
                  }
                : null
            }
            onChange={(opt) => setSelectedPart(opt ? opt.originalPart : null)}
            placeholder={t("equipmentTemplates.searchPartPlaceholder")}
            isClearable
            isSearchable
            noOptionsMessage={() => t("equipmentTemplates.noPartsFound")}
            formatOptionLabel={formatPartOptionLabel}
            styles={partSelectStyles}
          />
        </div>

        <input
          type="number"
          value={partQuantity}
          onChange={(e) => setPartQuantity(e.target.value)}
          className="input-field w-20 text-center"
          placeholder={t("equipmentTemplates.qty")}
          min="1"
        />

        <button
          type="button"
          onClick={handleAddPart}
          disabled={!selectedPart}
          className="btn-success whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("equipmentTemplates.addPartButton")}
        </button>
      </div>

      {selectedPart && (
        <div className="text-xs text-gray-500 pl-1">
          {t("equipmentTemplates.selectedLabel")}{" "}
          <span className="font-medium text-gray-700">{selectedPart.name}</span>
          {selectedPart.color && (
            <span className="ml-1">({selectedPart.color})</span>
          )}
          {selectedPart.sku && (
            <span className="ml-2 text-gray-400">{selectedPart.sku}</span>
          )}
          <span className="ml-2 text-green-600">
            CHF {selectedPart.purchase_price || 0}
          </span>
          <span className="ml-2 text-gray-400">
            {t("equipmentTemplates.stock")}: {selectedPart.quantity || 0}
          </span>
        </div>
      )}
    </div>
  );
  // ──────────────────────────────────────────────────────────

  // ── Component selector JSX (templates flagged as buildable components) ──
  const componentTemplateOptions = templates
    .filter((tpl) => tpl.is_component && tpl.id !== selectedTemplate?.id)
    .map((tpl) => ({
      value: tpl.id,
      label: `${tpl.name}${tpl.brand ? ` (${tpl.brand})` : ""}`,
      originalTemplate: tpl,
    }));

  const componentSelectorJSX = (
    <div className="space-y-2">
      <div className="flex gap-2 items-center">
        <div className="flex-1 min-w-0">
          <Select
            classNamePrefix="react-select"
            options={componentTemplateOptions}
            value={
              selectedComponentTemplate
                ? {
                    value: selectedComponentTemplate.id,
                    label: selectedComponentTemplate.name,
                    originalTemplate: selectedComponentTemplate,
                  }
                : null
            }
            onChange={(opt) =>
              setSelectedComponentTemplate(opt ? opt.originalTemplate : null)
            }
            placeholder={t("equipmentTemplates.searchComponentPlaceholder")}
            isClearable
            isSearchable
            noOptionsMessage={() => t("equipmentTemplates.noComponentTemplatesFound")}
            styles={partSelectStyles}
          />
        </div>

        <input
          type="number"
          value={componentQuantity}
          onChange={(e) => setComponentQuantity(e.target.value)}
          className="input-field w-20 text-center"
          placeholder={t("equipmentTemplates.qty")}
          min="1"
        />

        <button
          type="button"
          onClick={handleAddComponent}
          disabled={!selectedComponentTemplate}
          className="btn-success whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("equipmentTemplates.addComponentButton")}
        </button>
      </div>
    </div>
  );
  // ──────────────────────────────────────────────────────────

  return (
    <div>
      {/* Global Fetching Overlay */}
      {isFetching && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
          <LoadingSpinner />
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t("equipmentTemplates.title")}
          </h1>
          <p className="text-gray-500">
            {t("equipmentTemplates.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrintList}
            className="btn-secondary"
            disabled={filteredTemplates.length === 0}
          >
            🖨️ {t("common.print")}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            {t("equipmentTemplates.createTemplateButton")}
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filteredTemplates.length === 0 ? (
        <EmptyState
          onAction={() => setShowCreateModal(true)}
          hasItems={templates.length > 0}
        />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("common.name")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("common.category")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("equipmentTemplates.articleId")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("equipmentTemplates.parts")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("common.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTemplates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    {template.name}
                    {template.is_component && (
                      <span className="ml-2 inline-block px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">
                        {t("equipmentTemplates.componentBadge")}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {template.category || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {template.article_id || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {t("equipmentTemplates.partsCount", { count: template.parts_data?.length || 0 })}
                    {template.components_data?.length > 0 && (
                      <span className="ml-2 text-gray-400">
                        · {t("equipmentTemplates.componentsCount", { count: template.components_data.length })}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                    <button
                      onClick={() => openCreateEquipmentModal(template)}
                      className="text-green-600 hover:text-green-900"
                    >
                      🔧 {t("equipmentTemplates.use")}
                    </button>
                    <button
                      onClick={() => openDetailsModal(template)}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      {t("common.view")}
                    </button>
                    <button
                      onClick={() => openEditModal(template)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      {t("common.edit")}
                    </button>
                    <button
                      onClick={() => openDuplicateModal(template)}
                      className="text-purple-600 hover:text-purple-900"
                    >
                      {t("equipmentTemplates.duplicate")}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowDeleteDialog(true);
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      {t("common.delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create Modal ── */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title={
          isDuplicating
            ? t("equipmentTemplates.duplicateTemplateModalTitle")
            : t("equipmentTemplates.createTemplateModalTitle")
        }
        size="lg"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("equipmentTemplates.templateNameLabel")}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("common.category")}
              </label>
              <DatalistInput
                id="category-input"
                value={formData.category}
                onChange={(v) =>
                  setFormData((prev) => ({ ...prev, category: v }))
                }
                options={categories}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("equipmentTemplates.brand")}
              </label>
              <DatalistInput
                id="brands-input"
                value={formData.brand}
                onChange={(v) => setFormData((prev) => ({ ...prev, brand: v }))}
                options={brands}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("equipmentTemplates.articleId")}
              </label>
              <input
                type="text"
                value={formData.article_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    article_id: e.target.value,
                  }))
                }
                className="input-field"
                placeholder={t("equipmentTemplates.articleIdPlaceholder")}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("common.description")}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="input-field"
                rows="2"
              />
            </div>
          </div>

          {/* Parts */}
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t("equipmentTemplates.partsWithCount", { count: formData.parts_data.length })}
            </h3>
            {partSelectorJSX}
            <div className="mt-4">
              <PartsListTable
                parts={formData.parts_data}
                onRemove={removePart}
                onUpdate={updatePartQuantity}
              />
            </div>
          </div>

          {/* Is this template itself a component? */}
          <div className="flex items-center gap-2 pt-2 border-t">
            <input
              id="is-component-create"
              type="checkbox"
              checked={formData.is_component}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, is_component: e.target.checked }))
              }
              className="h-4 w-4"
            />
            <label htmlFor="is-component-create" className="text-sm text-gray-700">
              {t("equipmentTemplates.isComponentLabel")}
            </label>
          </div>

          {/* Required components (finished sub-assemblies built from other templates) */}
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t("equipmentTemplates.componentsWithCount", { count: formData.components_data.length })}
            </h3>
            {componentSelectorJSX}
            <div className="mt-4">
              <ComponentsListTable
                components={formData.components_data}
                onRemove={removeComponent}
                onUpdate={updateComponentQuantity}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              className="btn-secondary"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={formData.parts_data.length === 0}
            >
              {t("equipmentTemplates.createTemplateSubmit")}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          resetForm();
        }}
        title={t("equipmentTemplates.editTemplateModalTitle")}
        size="lg"
      >
        <form onSubmit={handleEdit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("equipmentTemplates.templateNameLabel")}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("common.category")}
              </label>
              <DatalistInput
                id="category-input"
                value={formData.category}
                onChange={(v) =>
                  setFormData((prev) => ({ ...prev, category: v }))
                }
                options={categories}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("equipmentTemplates.brand")}
              </label>
              <DatalistInput
                id="brands-input"
                value={formData.brand}
                onChange={(v) => setFormData((prev) => ({ ...prev, brand: v }))}
                options={brands}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("equipmentTemplates.articleId")}
              </label>
              <input
                type="text"
                value={formData.article_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    article_id: e.target.value,
                  }))
                }
                className="input-field"
                placeholder={t("equipmentTemplates.articleIdPlaceholder")}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("common.description")}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="input-field"
                rows="2"
              />
            </div>
          </div>

          {/* Parts */}
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t("equipmentTemplates.partsWithCount", { count: formData.parts_data.length })}
            </h3>
            {partSelectorJSX}
            <div className="mt-4">
              <PartsListTable
                parts={formData.parts_data}
                onRemove={removePart}
                onUpdate={updatePartQuantity}
              />
            </div>
          </div>

          {/* Is this template itself a component? */}
          <div className="flex items-center gap-2 pt-2 border-t">
            <input
              id="is-component-edit"
              type="checkbox"
              checked={formData.is_component}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, is_component: e.target.checked }))
              }
              className="h-4 w-4"
            />
            <label htmlFor="is-component-edit" className="text-sm text-gray-700">
              {t("equipmentTemplates.isComponentLabel")}
            </label>
          </div>

          {/* Required components (finished sub-assemblies built from other templates) */}
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t("equipmentTemplates.componentsWithCount", { count: formData.components_data.length })}
            </h3>
            {componentSelectorJSX}
            <div className="mt-4">
              <ComponentsListTable
                components={formData.components_data}
                onRemove={removeComponent}
                onUpdate={updateComponentQuantity}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}
              className="btn-secondary"
            >
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary">
              {t("equipmentTemplates.saveChanges")}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Details Modal ── */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={t("equipmentTemplates.templateDetailsModalTitle")}
        size="lg"
      >
        {selectedTemplate && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">{t("equipmentTemplates.templateNameField")}</p>
                <p className="font-medium">{selectedTemplate.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t("common.id")}</p>
                <p className="font-medium">{selectedTemplate.id || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t("common.category")}</p>
                <p className="font-medium">
                  {selectedTemplate.category || "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t("equipmentTemplates.brand")}</p>
                <p className="font-medium">{selectedTemplate.brand || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t("equipmentTemplates.articleId")}</p>
                <p className="font-medium">
                  {selectedTemplate.article_id || "-"}
                </p>
              </div>
              {selectedTemplate.description && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">{t("common.description")}</p>
                  <p className="font-medium">{selectedTemplate.description}</p>
                </div>
              )}
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-3">{t("equipmentTemplates.partsConfiguration")}</p>
              <PartsListTable
                parts={selectedTemplate.parts_data || []}
                showRemove={false}
              />
            </div>

            {selectedTemplate.components_data?.length > 0 && (
              <div>
                <p className="text-sm text-gray-500 mb-3">
                  {t("equipmentTemplates.componentsConfiguration")}
                </p>
                <ComponentsListTable
                  components={selectedTemplate.components_data}
                  showActions={false}
                />
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="btn-secondary"
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete Confirm ── */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t("equipmentTemplates.deleteTemplateTitle")}
        message={t("equipmentTemplates.deleteTemplateMessage", { name: selectedTemplate?.name })}
      />

      {/* ── Use Template / Create Equipment Modal ── */}
      <Modal
        isOpen={showCreateEquipmentModal}
        onClose={() => {
          setShowCreateEquipmentModal(false);
          setSelectedTemplate(null);
          setEquipmentQuantity(1);
          setEquipmentSerialNumbers([""]);
        }}
        title={t("equipmentTemplates.useTemplateModalTitle")}
        size="md"
      >
        <div className="space-y-4">
          {/* Template selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("equipmentTemplates.templateLabel")}
            </label>
            <Select
              classNamePrefix="react-select"
              options={templates.map((tpl) => ({
                value: tpl.id,
                label: `${tpl.article_id || t("equipmentTemplates.noIdFallback")} — ${tpl.name}`,
                originalTemplate: tpl,
              }))}
              value={
                selectedTemplate
                  ? {
                      value: selectedTemplate.id,
                      label: `${selectedTemplate.article_id || t("equipmentTemplates.noIdFallback")} — ${selectedTemplate.name}`,
                      originalTemplate: selectedTemplate,
                    }
                  : null
              }
              onChange={async (opt) => {
                if (!opt) {
                  setSelectedTemplate(null);
                  return;
                }
                setIsFetching(true);
                try {
                  const response = await equipmentTemplatesAPI.getById(
                    opt.originalTemplate.id,
                  );
                  const fullData = response.data.data;
                  if (typeof fullData.parts_data === "string") {
                    fullData.parts_data = JSON.parse(fullData.parts_data);
                  }
                  setSelectedTemplate(fullData);
                  setEquipmentQuantity(1);
                  setEquipmentSerialNumbers([""]);
                } catch (err) {
                  alert(t("equipmentTemplates.errorLoadingTemplate", { message: err.message }));
                } finally {
                  setIsFetching(false);
                }
              }}
              placeholder={t("equipmentTemplates.searchTemplatePlaceholder")}
              isClearable
              isSearchable
              noOptionsMessage={() => t("equipmentTemplates.noTemplatesFound")}
              formatOptionLabel={(option) => {
                const tpl = option.originalTemplate;
                if (!tpl) return option.label;
                return (
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium text-gray-900">
                        {tpl.name}
                      </span>
                      {tpl.brand && (
                        <span className="text-gray-500 text-xs ml-2">
                          {tpl.brand}
                        </span>
                      )}
                    </div>
                    <div className="text-right text-xs">
                      {tpl.article_id && (
                        <span className="text-blue-600 font-mono font-medium">
                          {tpl.article_id}
                        </span>
                      )}
                      <span className="text-gray-400 ml-2">
                        {t("equipmentTemplates.partsCount", { count: tpl.parts_data?.length || 0 })}
                      </span>
                    </div>
                  </div>
                );
              }}
              styles={partSelectStyles}
            />
          </div>

          {/* Parts preview — shows what will be consumed */}
          {selectedTemplate?.parts_data?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t("equipmentTemplates.partsToBeConsumed")}
              </p>
              <div className="rounded-md border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("equipmentTemplates.part")}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("equipmentTemplates.sku")}
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                        {t("equipmentTemplates.qty")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {selectedTemplate.parts_data.map((p, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-gray-800 font-medium">
                          {p.name}
                          {p.color && (
                            <span className="text-gray-400 ml-1 text-xs">
                              ({p.color})
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          {p.sku || "-"}
                        </td>
                        <td className="px-4 py-2 text-center font-semibold text-gray-700">
                          {p.quantity}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Components preview — informational only: stock is just
              subtracted automatically on submit, no per-unit picking, and
              it's fine if this takes a component's stock negative. */}
          {selectedTemplate?.components_data?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t("equipmentTemplates.componentsToBeConsumed")}
              </p>
              <div className="rounded-md border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("equipmentTemplates.component")}
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                        {t("equipmentTemplates.qty")}
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                        {t("equipmentTemplates.currentStock")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {selectedTemplate.components_data.map((c) => {
                      const totalNeeded = c.quantity * equipmentQuantity;
                      const short = c.available_stock < totalNeeded;
                      return (
                        <tr key={c.component_template_id}>
                          <td className="px-4 py-2 text-gray-800 font-medium">
                            {c.template_name}
                          </td>
                          <td className="px-4 py-2 text-center text-gray-700">
                            {totalNeeded}
                          </td>
                          <td
                            className={`px-4 py-2 text-center font-semibold ${
                              short ? "text-red-600" : "text-gray-700"
                            }`}
                          >
                            {c.available_stock}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {selectedTemplate.components_data.some(
                (c) => c.available_stock < c.quantity * equipmentQuantity,
              ) && (
                <p className="text-xs text-amber-600 mt-1">
                  {t("equipmentTemplates.componentStockWillGoNegative")}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("equipment.quantity")}
            </label>
            <input
              type="number"
              min="1"
              value={equipmentQuantity}
              onChange={(e) => handleEquipmentQuantityChange(e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                {t("equipment.serialNumbers")}
              </label>
              <button
                type="button"
                onClick={addEquipmentSerialField}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                + {t("equipment.addSerialNumber")}
              </button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {equipmentSerialNumbers.map((serial, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <span className="text-sm text-gray-400 w-6 text-right">
                    {index + 1}.
                  </span>
                  <input
                    type="text"
                    value={serial}
                    onChange={(e) =>
                      updateEquipmentSerialNumber(index, e.target.value)
                    }
                    className="input-field flex-1"
                    placeholder={t("equipment.serialNumberOptional")}
                  />
                  <button
                    type="button"
                    onClick={() => removeEquipmentSerialField(index)}
                    disabled={equipmentSerialNumbers.length <= 1}
                    className="px-2 py-1 text-red-600 hover:text-red-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                    aria-label={t("equipment.removeSerialNumber")}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleCreateEquipment}
            className="btn-primary w-full"
            disabled={!selectedTemplate || creatingEquipment}
          >
            {equipmentQuantity > 1
              ? t("equipment.createMultiple", { count: equipmentQuantity })
              : t("equipmentTemplates.createEquipmentButton")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default EquipmentTemplates;
